/**
 * UMD 入口文件
 * 
 * 本文件是渲染器模块的 UMD（Universal Module Definition）入口点。
 * 主要功能：
 * - 导出所有核心类和函数供 ES6 模块使用
 * - 在浏览器环境中将 API 暴露到全局 window 对象
 * - 支持通过 <script> 标签直接引入使用
 * 
 * 使用示例（浏览器环境）：
 * ```html
 * <script src="pdf-badge-renderer.umd.js"></script>
 * <script>
 *   const renderer = PDFBadgeRenderer.createRenderer();
 *   // 或者
 *   const renderer = new PDFBadgeRenderer.TemplateRenderer();
 * </script>
 * ```
 */

import { TemplateRenderer } from './canvas-renderer';
import { generateBarcode, generateQRCode } from './barcode-generator';
import { UnitConverter } from './unit-converter';
import type { TemplateConfig, TemplateObject, RenderOptions, ProgressCallback } from './types';

/**
 * 导出所有类和函数
 * 这些导出可以在 ES6 模块环境中使用
 */
export {
    TemplateRenderer,
    generateBarcode,
    generateQRCode,
    UnitConverter
};

/**
 * 导出类型定义
 * 仅用于 TypeScript 项目的类型检查
 */
export type {
    TemplateConfig,
    TemplateObject,
    RenderOptions,
    ProgressCallback
};

/**
 * 在浏览器环境中暴露全局 API
 * 
 * 将所有核心功能挂载到 window.PDFBadgeRenderer 对象上，
 * 使得可以通过 <script> 标签直接引入并使用。
 */
if (typeof window !== 'undefined') {
    (window as any).PDFBadgeRenderer = {
        /** 模板渲染器类 */
        TemplateRenderer,

        /** 条形码生成函数 */
        generateBarcode,

        /** 二维码生成函数 */
        generateQRCode,

        /** 单位转换工具类 */
        UnitConverter,

        /**
         * 便捷方法：创建渲染器实例
         * @returns 新的 TemplateRenderer 实例
         */
        createRenderer: () => new TemplateRenderer()
    };
}
